import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import PetsmateApp from "@/components/PetsmateApp";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function LoginScreen({ onLogin }) {
  const googleButtonRef = useRef(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadGoogle = () => {
      if (window.google?.accounts?.id) {
        if (!cancelled && googleButtonRef.current) {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: async (response) => {
              try {
                setError("");

                const res = await fetch("/api/auth/google", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    credential: response.credential,
                  }),
                });

                const data = await res.json();

                if (!res.ok) {
                  throw new Error(
                    data?.detail || "Google login failed"
                  );
                }

                localStorage.setItem("vetprescribe_token", data.token);
                localStorage.setItem(
                  "vetprescribe_user",
                  JSON.stringify(data.user)
                );

                onLogin(data.user);
              } catch (err) {
                console.error(err);
                setError(err.message || "Google login failed");
              }
            },
          });

          googleButtonRef.current.innerHTML = "";

          window.google.accounts.id.renderButton(
            googleButtonRef.current,
            {
              theme: "outline",
              size: "large",
              shape: "rectangular",
              text: "signin_with",
              width: 320,
            }
          );

          setLoading(false);
        }

        return true;
      }

      return false;
    };

    if (!loadGoogle()) {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;

      script.onload = loadGoogle;

      document.head.appendChild(script);

      return () => {
        cancelled = true;
      };
    }

    setLoading(false);

    return () => {
      cancelled = true;
    };
  }, [onLogin]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-[#0F4C3A] text-white flex items-center justify-center text-2xl">
            🐾
          </div>

          <h1 className="text-3xl font-semibold text-[#1A1F1D]">
            VetPrescribe
          </h1>

          <p className="mt-2 text-[#5C6B66]">
            Veterinary prescribing made simple
          </p>
        </div>

        <div className="border border-[#E2E8F0] rounded-2xl p-8 shadow-sm bg-white">
          <h2 className="text-xl font-semibold text-[#1A1F1D] text-center">
            Welcome
          </h2>

          <p className="text-sm text-[#5C6B66] text-center mt-2 mb-7">
            Sign in with your Google account to continue.
          </p>

          <div className="flex justify-center min-h-[48px]">
            <div ref={googleButtonRef} />
          </div>

          {loading && (
            <p className="text-sm text-[#5C6B66] text-center mt-4">
              Loading Google Sign-In…
            </p>
          )}

          {error && (
            <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <p className="text-xs text-[#7A8883] text-center mt-7">
            By continuing, you agree to use VetPrescribe for authorized
            veterinary clinical work.
          </p>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem("vetprescribe_user");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const handleLogout = () => {
    localStorage.removeItem("vetprescribe_token");
    localStorage.removeItem("vetprescribe_user");
    setUser(null);
  };

  if (!user) {
    return <LoginScreen onLogin={setUser} />;
  }

  return (
    <>
      <PetsmateApp />
    </>
  );
}

export default App;
